import React, { useState, useRef, useEffect } from 'react';
import { VariantData, ChatMessage } from '../types';
import { chatAboutVariant, generateMessageId, getSuggestedQuestions } from '../services/chatService';

interface VariantChatProps {
  variantData: VariantData;
}

export function VariantChat({ variantData }: VariantChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestedQuestions = getSuggestedQuestions(variantData);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset chat when variant changes
  useEffect(() => {
    setMessages([]);
    setError(null);
  }, [variantData.gene, variantData.proteinChange]);

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading) return;

    setError(null);
    setInputValue('');

    // Add user message
    const userMessage: ChatMessage = {
      id: generateMessageId(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await chatAboutVariant(text, variantData, messages);

      // Add assistant message
      const assistantMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    handleSendMessage(question);
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div
        style={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={styles.headerLeft}>
          <span style={styles.chatIcon}>💬</span>
          <span style={styles.headerTitle}>Let's talk about this :)</span>
          {messages.length > 0 && (
            <span style={styles.messageCount}>{messages.length}</span>
          )}
        </div>
        <div style={styles.headerRight}>
          {messages.length > 0 && (
            <button
              style={styles.clearButton}
              onClick={(e) => { e.stopPropagation(); clearChat(); }}
              title="Clear chat"
            >
              🗑️
            </button>
          )}
          <span style={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {/* Expanded Chat Area */}
      {isExpanded && (
        <div style={styles.chatArea}>
          {/* Messages */}
          <div style={styles.messagesContainer}>
            {messages.length === 0 ? (
              <div style={styles.emptyState}>
                {/* Suggested question buttons only - no extra text */}
                <div style={styles.suggestedQuestions}>
                  {suggestedQuestions.map((q, idx) => (
                    <button
                      key={idx}
                      style={styles.suggestionButton}
                      onClick={() => handleSuggestedQuestion(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    style={{
                      ...styles.message,
                      ...(msg.role === 'user' ? styles.userMessage : styles.assistantMessage),
                    }}
                  >
                    <div style={styles.messageRole}>
                      {msg.role === 'user' ? '👤 You' : '🧬 Assistant'}
                    </div>
                    <div style={styles.messageContent}>
                      {msg.content.split('\n').map((line, i) => (
                        <p key={i} style={styles.messageLine}>{line}</p>
                      ))}
                    </div>
                    <div style={styles.messageTime}>
                      {msg.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div style={{ ...styles.message, ...styles.assistantMessage }}>
                    <div style={styles.messageRole}>🧬 Assistant</div>
                    <div style={styles.loadingDots}>
                      <span>●</span><span>●</span><span>●</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div style={styles.error}>
              ⚠️ {error}
            </div>
          )}

          {/* Input Area */}
          <div style={styles.inputArea}>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about this variant, the condition, management, or draft a letter..."
              style={styles.input}
              disabled={isLoading}
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim() || isLoading}
              style={{
                ...styles.sendButton,
                ...((!inputValue.trim() || isLoading) ? styles.sendButtonDisabled : {}),
              }}
            >
              {isLoading ? '...' : '➤'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    marginTop: '20px',
    border: '1px solid #e0e0e0',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: '#f8f9fa',
    cursor: 'pointer',
    borderBottom: '1px solid #e0e0e0',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  chatIcon: {
    fontSize: '20px',
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
  },
  messageCount: {
    backgroundColor: '#6366f1',
    color: 'white',
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: '600',
  },
  clearButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '4px',
    opacity: 0.6,
  },
  expandIcon: {
    fontSize: '12px',
    color: '#666',
  },
  chatArea: {
    display: 'flex',
    flexDirection: 'column',
    height: '400px',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    backgroundColor: '#fafafa',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    padding: '20px',
  },
  suggestedQuestions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
    maxWidth: '400px',
  },
  suggestionButton: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    color: '#444',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.2s',
  },
  message: {
    marginBottom: '16px',
    padding: '12px 16px',
    borderRadius: '12px',
    maxWidth: '85%',
  },
  userMessage: {
    backgroundColor: '#6366f1',
    color: 'white',
    marginLeft: 'auto',
    borderBottomRightRadius: '4px',
  },
  assistantMessage: {
    backgroundColor: '#ffffff',
    color: '#333',
    border: '1px solid #e0e0e0',
    marginRight: 'auto',
    borderBottomLeftRadius: '4px',
  },
  messageRole: {
    fontSize: '11px',
    fontWeight: '600',
    marginBottom: '6px',
    opacity: 0.8,
  },
  messageContent: {
    fontSize: '14px',
    lineHeight: '1.5',
  },
  messageLine: {
    margin: '0 0 8px 0',
  },
  messageTime: {
    fontSize: '10px',
    opacity: 0.6,
    marginTop: '8px',
    textAlign: 'right',
  },
  loadingDots: {
    display: 'flex',
    gap: '4px',
    animation: 'pulse 1.5s infinite',
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '10px 16px',
    fontSize: '13px',
    borderTop: '1px solid #fecaca',
  },
  inputArea: {
    display: 'flex',
    gap: '10px',
    padding: '16px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fff',
  },
  input: {
    flex: 1,
    padding: '12px 16px',
    fontSize: '14px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
  },
  sendButton: {
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 20px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  sendButtonDisabled: {
    backgroundColor: '#c7c7c7',
    cursor: 'not-allowed',
  },
};

export default VariantChat;